import { useCallback, useRef } from 'react';
import {
  useMutation as useQueryMutation,
  type DefaultError,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

/** Plain data only: anything else (a File, a function) is the same only when it is the very same thing. */
function isPlain(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Whether two sets of mutation variables ask for the same thing. */
export function sameRequest(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => sameRequest(item, b[i]));
  }
  if (!isPlain(a) || !isPlain(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => sameRequest(a[k], b[k]));
}

/**
 * React Query's `useMutation`, sending a thing once: a second `mutate` with the same variables
 * while the first is still on its way is dropped. A fast double click, or Enter and a click, both
 * land before the screen has drawn its disabled button (the pending state arrives a tick later),
 * and would otherwise buy the pack, make the key or send the invitation twice. Different variables
 * are a different request and go through; so does a retry once the first has settled.
 */
export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const flying = useRef<TVariables[]>([]);
  const settled = options.onSettled;
  const mutation = useQueryMutation<TData, TError, TVariables, TContext>({
    ...options,
    onSettled: (data, error, variables, ...rest) => {
      const at = flying.current.indexOf(variables);
      if (at >= 0) flying.current.splice(at, 1);
      return settled?.(data, error, variables, ...rest);
    },
  });
  const send = mutation.mutate;
  const mutate = useCallback(
    (...args: Parameters<typeof send>) => {
      const variables = args[0] as TVariables;
      if (flying.current.some((sent) => sameRequest(sent, variables))) return;
      flying.current.push(variables);
      send(...args);
    },
    [send],
  );
  return { ...mutation, mutate } as UseMutationResult<TData, TError, TVariables, TContext>;
}
